/**
 * Clients List Page
 * Main page for viewing and managing clients
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { MobileDataList } from '../../../components/mobile';
import { useClients, useDeleteClient, useCreateClient } from '../hooks/useClients';
import ClientTierBadge from '../components/ClientTierBadge';
import ClientStatusChip from '../components/ClientStatusChip';
import HealthScoreIndicator from '../components/HealthScoreIndicator';
import CreateClientDialog, { type CreateClientFormData } from '../components/CreateClientDialog';
import AttributeSelectionDialog from '../components/AttributeSelectionDialog';
import type { ClientStatus, ClientTier } from '../types/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

export const ClientsPage: React.FC = () => {
  const navigate = useNavigate();

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 900px)');

  // Filters state
  const [statusFilter, setStatusFilter] = useState<ClientStatus | ''>('');
  const [tierFilter, setTierFilter] = useState<ClientTier | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const [pendingClientData, setPendingClientData] = useState<CreateClientFormData | null>(null);
  const [attrDialogLoading, setAttrDialogLoading] = useState(false);

  // Fetch clients with filters
  const { data, isLoading, error, refetch } = useClients({
    status: statusFilter || undefined,
    tier: tierFilter || undefined,
    search: searchQuery || undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });

  // Mutations
  const deleteClientMutation = useDeleteClient();
  const createClientMutation = useCreateClient();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value as ClientStatus | '');
    setPage(0);
  };

  const handleTierFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTierFilter(event.target.value as ClientTier | '');
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClient = (clientId: string) => {
    navigate(`/ai-crm/clients/${clientId}`);
  };

  const handleDeleteClient = async (clientId: string, clientName: string) => {
    if (window.confirm(`Are you sure you want to delete client "${clientName}"? This action cannot be undone.`)) {
      try {
        await deleteClientMutation.mutateAsync(clientId);
        toast.success('Client deleted successfully');
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to delete client');
      }
    }
  };

  const handleCreateClient = async (formData: CreateClientFormData) => {
    setPendingClientData(formData);
    setCreateDialogOpen(false);
    setAttrDialogOpen(true);
  };

  const handleConfirmCreateClient = async (attributes: Record<string, string>) => {
    if (!pendingClientData) return;
    setAttrDialogLoading(true);
    try {
      const newClient = await createClientMutation.mutateAsync({
        leadId: '',
        clientName: pendingClientData.clientName,
        clientType: pendingClientData.clientType,
        industry: pendingClientData.industry || undefined,
        companySize: pendingClientData.companySize || undefined,
        website: pendingClientData.website || undefined,
        primaryContact: pendingClientData.primaryContact,
        email: pendingClientData.email,
        phone: pendingClientData.phone || undefined,
        businessAddress: pendingClientData.businessAddress || undefined,
        tags: pendingClientData.tags.length > 0 ? pendingClientData.tags : undefined,
        customFields: Object.keys(attributes).length > 0 ? attributes : undefined,
      });

      toast.success(`Client "${pendingClientData.clientName}" created successfully!`);
      setAttrDialogOpen(false);
      setPendingClientData(null);

      if (newClient?.id) {
        navigate(`/ai-crm/clients/${newClient.id}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create client');
    } finally {
      setAttrDialogLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD'
    }).format(value);
  };

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'clientName' as const,
    secondaryField: 'primaryContact' as const,
    statusField: 'status' as const,
    statusConfig: {
      ACTIVE: { color: '#52c41a', label: 'Active' },
      INACTIVE: { color: '#d9d9d9', label: 'Inactive' },
      CHURNED: { color: '#ff4d4f', label: 'Churned' },
      AT_RISK: { color: '#faad14', label: 'At Risk' },
      VIP: { color: '#1890ff', label: 'VIP' },
    },
    avatar: {
      field: 'clientName' as const,
      type: 'text' as const,
    },
    fields: [
      { key: 'email' as const, label: 'Email' },
      { key: 'tier' as const, label: 'Tier' },
      { key: 'healthScore' as const, label: 'Health', render: (value: number) => `${value || 0}%` },
      { key: 'lifetimeValue' as const, label: 'LTV', render: (value: number) => formatCurrency(value || 0) },
    ],
    onClick: (client: any) => handleViewClient(client.id),
  };

  // Mobile filters
  const mobileFilters = [
    {
      key: 'status',
      placeholder: 'Status',
      options: [
        { label: 'All', value: '' },
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Inactive', value: 'INACTIVE' },
        { label: 'At Risk', value: 'AT_RISK' },
        { label: 'VIP', value: 'VIP' },
        { label: 'Churned', value: 'CHURNED' },
      ],
      value: statusFilter,
      onChange: (value: string) => {
        setStatusFilter(value as ClientStatus | '');
        setPage(0);
      },
    },
    {
      key: 'tier',
      placeholder: 'Tier',
      options: [
        { label: 'All', value: '' },
        { label: 'Diamond', value: 'DIAMOND' },
        { label: 'Platinum', value: 'PLATINUM' },
        { label: 'Gold', value: 'GOLD' },
        { label: 'Silver', value: 'SILVER' },
        { label: 'Bronze', value: 'BRONZE' },
      ],
      value: tierFilter,
      onChange: (value: string) => {
        setTierFilter(value as ClientTier | '');
        setPage(0);
      },
    },
  ];

  // Mobile View
  if (isMobile) {
    return (
      <Box sx={{ pb: 2 }}>
        {/* Mobile Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Clients
          </Typography>
          <Box display="flex" gap={1}>
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load clients.
          </Alert>
        )}

        {/* Mobile Summary Stats */}
        {!isLoading && data && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h5" fontWeight="bold">{data.pagination.total}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Clients</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {data.data.filter(c => c.status === 'VIP').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">VIP</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Mobile Data List */}
        <MobileDataList
          data={data?.data || []}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={isLoading}
          emptyText="No clients found"
          emptyDescription="Try adjusting your filters or add a new client"
          onRefresh={() => refetch()}
          searchPlaceholder="Search clients..."
          onSearch={(value) => {
            setSearchQuery(value);
            setPage(0);
          }}
          filters={mobileFilters}
          showFilters={true}
          pagination={{
            current: page + 1,
            pageSize: rowsPerPage,
            total: data?.pagination.total || 0,
            onChange: (newPage, pageSize) => {
              setPage(newPage - 1);
              setRowsPerPage(pageSize);
            },
          }}
          swipeActions={{
            left: {
              icon: <DeleteIcon />,
              color: '#ff4d4f',
              label: 'Delete',
              onClick: (client) => handleDeleteClient(client.id, client.clientName),
            },
            right: {
              icon: <ViewIcon />,
              color: '#1890ff',
              label: 'View',
              onClick: (client) => handleViewClient(client.id),
            },
          }}
        />

        {/* Create Client Dialog */}
        <CreateClientDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={handleCreateClient}
          isLoading={createClientMutation.isPending}
        />
      </Box>
    );
  }

  // Desktop View
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Clients
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Client
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Search clients..."
              value={searchQuery}
              onChange={handleSearch}
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            {/* Status Filter */}
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="AT_RISK">At Risk</MenuItem>
              <MenuItem value="CHURNED">Churned</MenuItem>
              <MenuItem value="VIP">VIP</MenuItem>
            </TextField>

            {/* Tier Filter */}
            <TextField
              select
              label="Tier"
              value={tierFilter}
              onChange={handleTierFilterChange}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Tiers</MenuItem>
              <MenuItem value="BRONZE">Bronze</MenuItem>
              <MenuItem value="SILVER">Silver</MenuItem>
              <MenuItem value="GOLD">Gold</MenuItem>
              <MenuItem value="PLATINUM">Platinum</MenuItem>
              <MenuItem value="DIAMOND">Diamond</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent>
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load clients. Please try again.
            </Alert>
          )}

          {/* Data Table */}
          {!isLoading && !error && data && (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Client Name</strong></TableCell>
                      <TableCell><strong>Contact</strong></TableCell>
                      <TableCell><strong>Tier</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Health Score</strong></TableCell>
                      <TableCell align="right"><strong>LTV</strong></TableCell>
                      <TableCell align="right"><strong>Total Revenue</strong></TableCell>
                      <TableCell align="center"><strong>Orders</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No clients found. Try adjusting your filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.data.map((client) => (
                        <TableRow
                          key={client.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewClient(client.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {client.clientName}
                            </Typography>
                            {client.industry && (
                              <Typography variant="caption" color="text.secondary">
                                {client.industry}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{client.primaryContact}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <ClientTierBadge tier={client.tier} />
                          </TableCell>
                          <TableCell>
                            <ClientStatusChip status={client.status} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <HealthScoreIndicator score={client.healthScore} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <MoneyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatCurrency(client.lifetimeValue)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(client.totalRevenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={client.totalOrders}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewClient(client.id)}
                                title="View Details"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClient(client.id, client.clientName)}
                                title="Delete Client"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {data.data.length > 0 && (
                <TablePagination
                  component="div"
                  count={data.pagination.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Clients
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {data.pagination.total}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Showing
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, data.pagination.total)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Create Client Dialog */}
      <CreateClientDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateClient}
        isLoading={createClientMutation.isPending}
      />

      {/* Attribute Selection Dialog (shown after client form, before creating) */}
      <AttributeSelectionDialog
        open={attrDialogOpen}
        onClose={() => {
          setAttrDialogOpen(false);
          setPendingClientData(null);
        }}
        onConfirm={handleConfirmCreateClient}
        loading={attrDialogLoading}
        entityName={pendingClientData?.clientName}
        appliesTo="CLIENT"
      />
    </Box>
  );
};

export default ClientsPage;
